import { apiRequest } from '../../../shared/services/apiClient';

export type EmailBrandHeaderStyle = 'plain' | 'band';
export type EmailBrandAlignment = 'left' | 'center';

export interface EmailBrandSocialLink {
  network: string;
  url: string;
}

export interface EmailBrandKit {
  enabled: boolean;
  logoUrl: string | null;
  logoAltText: string | null;
  logoWidth: number;
  headerStyle: EmailBrandHeaderStyle;
  headerAlignment: EmailBrandAlignment;
  primaryColor: string;
  backgroundColor: string;
  contentBackgroundColor: string;
  textColor: string;
  fontFamily: string;
  companyName: string | null;
  footerText: string | null;
  address: string | null;
  websiteUrl: string | null;
  socialLinks: EmailBrandSocialLink[];
}

export interface EmailBrandKitOptions {
  headerStyles: string[];
  alignments: string[];
  fonts: string[];
  socialNetworks: string[];
  minLogoWidth: number;
  maxLogoWidth: number;
}

export interface EmailBrandKitResponse {
  brandKit: EmailBrandKit;
  hasUploadedLogo: boolean;
  updatedAt: string | null;
  suggestions: { companyName: string | null; address: string | null; websiteUrl: string | null };
  options: EmailBrandKitOptions;
}

/** Text fields travel as-is: an empty string clears the value on the server. */
export type EmailBrandKitInput = Partial<Omit<EmailBrandKit, 'logoUrl' | 'logoAltText' | 'companyName' | 'footerText' | 'address' | 'websiteUrl'>> & {
  logoUrl?: string;
  logoAltText?: string;
  companyName?: string;
  footerText?: string;
  address?: string;
  websiteUrl?: string;
};

export interface EmailBrandPreview {
  html: string;
  text: string;
  htmlBytes: number;
  exceedsClipLimit: boolean;
}

const BASE = '/api/v1/email/branding';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const unwrap = (payload: unknown): Record<string, unknown> => {
  const record = asRecord(payload);
  return record.data && typeof record.data === 'object' ? asRecord(record.data) : record;
};

const optionalString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export const normalizeEmailBrandKit = (value: unknown): EmailBrandKit => {
  const kit = asRecord(value);
  return {
    enabled: Boolean(kit.enabled),
    logoUrl: optionalString(kit.logoUrl),
    logoAltText: optionalString(kit.logoAltText),
    logoWidth: Number(kit.logoWidth ?? 140) || 140,
    headerStyle: kit.headerStyle === 'band' ? 'band' : 'plain',
    headerAlignment: kit.headerAlignment === 'center' ? 'center' : 'left',
    primaryColor: String(kit.primaryColor ?? '#2563eb'),
    backgroundColor: String(kit.backgroundColor ?? '#f4f5f7'),
    contentBackgroundColor: String(kit.contentBackgroundColor ?? '#ffffff'),
    textColor: String(kit.textColor ?? '#1f2937'),
    fontFamily: String(kit.fontFamily ?? 'system'),
    companyName: optionalString(kit.companyName),
    footerText: optionalString(kit.footerText),
    address: optionalString(kit.address),
    websiteUrl: optionalString(kit.websiteUrl),
    socialLinks: (Array.isArray(kit.socialLinks) ? kit.socialLinks : [])
      .map(asRecord)
      .map(link => ({ network: String(link.network ?? ''), url: String(link.url ?? '') }))
      .filter(link => link.network && link.url),
  };
};

export const normalizeEmailBrandKitResponse = (payload: unknown): EmailBrandKitResponse => {
  const data = unwrap(payload);
  const suggestions = asRecord(data.suggestions);
  const options = asRecord(data.options);
  return {
    brandKit: normalizeEmailBrandKit(data.brandKit),
    hasUploadedLogo: Boolean(data.hasUploadedLogo),
    updatedAt: optionalString(data.updatedAt),
    suggestions: {
      companyName: optionalString(suggestions.companyName),
      address: optionalString(suggestions.address),
      websiteUrl: optionalString(suggestions.websiteUrl),
    },
    options: {
      headerStyles: stringList(options.headerStyles),
      alignments: stringList(options.alignments),
      fonts: stringList(options.fonts),
      socialNetworks: stringList(options.socialNetworks),
      minLogoWidth: Number(options.minLogoWidth ?? 40) || 40,
      maxLogoWidth: Number(options.maxLogoWidth ?? 320) || 320,
    },
  };
};

export const getEmailBranding = async (): Promise<EmailBrandKitResponse> =>
  normalizeEmailBrandKitResponse(await apiRequest<unknown>(BASE));

export const updateEmailBranding = async (input: EmailBrandKitInput): Promise<EmailBrandKitResponse> =>
  normalizeEmailBrandKitResponse(await apiRequest<unknown>(BASE, { method: 'PUT', body: JSON.stringify(input) }));

export const uploadEmailBrandLogo = async (file: File): Promise<EmailBrandKitResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return normalizeEmailBrandKitResponse(await apiRequest<unknown>(`${BASE}/logo`, { method: 'POST', body: formData }));
};

export const removeEmailBrandLogo = async (): Promise<EmailBrandKitResponse> =>
  normalizeEmailBrandKitResponse(await apiRequest<unknown>(`${BASE}/logo`, { method: 'DELETE' }));

export const previewEmailBranding = async (
  brandKit: EmailBrandKitInput,
  sample?: { subject?: string; body?: string; signature?: string },
): Promise<EmailBrandPreview> => {
  const data = unwrap(await apiRequest<unknown>(`${BASE}/preview`, {
    method: 'POST',
    body: JSON.stringify({ brandKit, ...sample }),
  }));
  return {
    html: String(data.html ?? ''),
    text: String(data.text ?? ''),
    htmlBytes: Number(data.htmlBytes ?? 0),
    exceedsClipLimit: Boolean(data.exceedsClipLimit),
  };
};
